// Profil-adatok szerkesztő (PATCH /profile) — Alapadatok · Kapcsolat (e-mail step-up) · Integrációk (Discord +
// Asana) · Értesítések, EGY RHF-form + EGY „Mentés" gomb. A resolver a @atlas/contracts sémájából épül. Az
// e-mail VÁLTOZÁSNÁL feltételes step-up jelszó-mező jelenik meg (a currentPassword CSAK ekkor kerül a body-ba).
import { type ProfileSchema } from '@atlas/contracts';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { DateField, TextField, SwitchField, PasswordField } from '@/components/shared/form-fields';
import { PersonChildrenField } from '@/components/shared/person-children-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ImpersonationFence } from '@/features/impersonation/components/impersonation-fence';
import { profileKeys } from '@/features/profile/api';
import { ProfileSection } from '@/features/profile/components/profile-section';
import {
  buildProfileResolver,
  isEmailChanged,
  profileFormToBody,
  profileToFormValues,
  type ProfileFormValues,
} from '@/features/profile/form';
import {
  buildPersonalResolver,
  personalFormToBody,
  personalToFormValues,
  type PersonalFormValues,
  type ProfilePersonal,
} from '@/features/profile/personal-form';
import { api } from '@/lib/api';
import { tryRefresh } from '@/lib/auth';
import { formatDay } from '@/lib/format-datetime';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type Profile = z.infer<typeof ProfileSchema>;
type ProfilePage = Awaited<ReturnType<typeof api.profile.query>>;

export interface ProfileDetailsFormProps {
  profile: Profile;
  /**
   * MELYIK FELÜLETEN jelenik meg. A LOGIKA (OCC-meta, e-mail step-up, optimista mentés, hiba-terelés) azonos —
   * ezért nem másoljuk le a partner-portálra —, a MEZŐ-KÉSZLET viszont felület-függő:
   *
   *  • `internal` (alap): becenév + Integrációk szakasz (Discord-azonosító, Asana-PAT) — belső munka-eszközök;
   *  • `partner`: ezek NÉLKÜL, és az e-mail csak OLVASHATÓ. A partner e-mailje a BELÉPÉSI azonosítója, amit a
   *    terv szerint nem maga írhat át (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9): a csere
   *    ügyintézői döntés, ezért a mező mellett a „nyisson jegyet" magyarázat áll.
   */
  surface?: 'internal' | 'partner';
  /**
   * A SAJÁT személyes adatok (születésnap, névnap, gyerekek) — az „Egyéb adatok" rész forrása. CSAK a belső
   * felületen adja át a route (a partner-portálon nincs ilyen adat, a végpont is elutasítja); `null`/hiány → a rész
   * nem jelenik meg (pl. munkatársi adatlap nélküli gépi fióknál), és nem is validálódik.
   */
  personal?: ProfilePersonal | null;
}

export function ProfileDetailsForm({
  profile,
  surface = 'internal',
  personal = null,
}: ProfileDetailsFormProps): JSX.Element {
  const isPartner = surface === 'partner';
  // az „Egyéb adatok" CSAK belsősnek és csak meglévő adatlappal (a partner-felületen semmilyen ágon nem él)
  const showPersonal = !isPartner && personal !== null;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Az Asana PAT (write-only titok) a KÖZÖS mentés részeként áll be — nincs külön mentés-gomb. A törlés viszont
  // önálló, azonnali akció (kuka ikon a label sorában). A plaintext SOHA nem töltődik vissza; üres input → nincs írás.
  const [asanaPat, setAsanaPat] = useState('');

  /** Az Asana PAT beállítása a fő mentés részeként — csak nem-üres inputnál, sikeres profil-mentés UTÁN.
   *  Nincs saját siker-toast (a fő „Mentés" toastja fedi); csak hibára jelez. */
  const commitAsanaPat = async (): Promise<void> => {
    const pat = asanaPat.trim();
    if (pat === '') return;
    try {
      const res = await api.profileSetAsanaPat.mutation({ body: { asanaPat: pat } });
      if (res.status === 200) {
        setAsanaPat('');
        void queryClient.invalidateQueries({ queryKey: profileKeys.detail() });
      } else {
        toast.error(t('toast:profile.saveError'));
      }
    } catch {
      toast.error(t('toast:profile.saveError'));
    }
  };

  /** Az Asana PAT törlése — önálló, azonnali akció (kuka ikon). Saját siker-toast. */
  const deleteAsanaPat = async (): Promise<void> => {
    try {
      const res = await api.profileClearAsanaPat.mutation({});
      if (res.status === 200) {
        setAsanaPat('');
        toast.success(t('toast:profile.asanaCleared'));
        void queryClient.invalidateQueries({ queryKey: profileKeys.detail() });
      } else {
        toast.error(t('toast:profile.saveError'));
      }
    } catch {
      toast.error(t('toast:profile.saveError'));
    }
  };

  const form = useForm<ProfileFormValues>({
    resolver: buildProfileResolver(t),
    mode: 'onTouched',
    defaultValues: profileToFormValues(profile),
  });
  const { reset } = form;
  const isDirty = form.formState.isDirty;

  // „Egyéb adatok" — KÜLÖN al-űrlap (saját végpont, saját OCC-verzió), de a KÖZÖS „Mentés" gomb menti.
  const personalForm = useForm<PersonalFormValues>({
    resolver: buildPersonalResolver(t),
    mode: 'onTouched',
    defaultValues: personal
      ? personalToFormValues(personal)
      : { birthDate: '', nameDay: '', children: [], celebrationsOptOut: false },
  });
  const personalDirty = personalForm.formState.isDirty;
  useEffect(() => {
    if (personal && !personalDirty) personalForm.reset(personalToFormValues(personal));
  }, [personal, personalDirty, personalForm]);

  /** Az „Egyéb adatok" mentése a fő mentés részeként — csak ha változott; saját hibajelzéssel (409 = elavult). */
  const commitPersonal = async (): Promise<void> => {
    if (!showPersonal || !personal || !personalForm.formState.isDirty) return;
    try {
      const res = await api.profilePersonalUpdate.mutation({
        body: personalFormToBody(personalForm.getValues(), personal.version),
      });
      if (res.status === 200) {
        personalForm.reset(personalToFormValues(res.body));
        queryClient.setQueryData(profileKeys.personal(), {
          status: 200,
          body: res.body,
          headers: new Headers(),
        });
      } else if (res.status === 409) {
        toast.error(t('toast:profile.personalConflict'));
      } else {
        toast.error(t('toast:profile.personalSaveError'));
      }
    } catch {
      toast.error(t('toast:profile.personalSaveError'));
    } finally {
      void queryClient.invalidateQueries({ queryKey: profileKeys.personal() });
    }
  };

  // Háttér-refetch (invalidálás után) → a form újratöltése, DE csak amíg a user nem piszkálta (ne írjuk felül a gépelést).
  useEffect(() => {
    if (!isDirty) reset(profileToFormValues(profile));
  }, [profile, isDirty, reset]);

  const emailNow = form.watch('email');
  const emailChanged = isEmailChanged({ email: emailNow }, profile.email);

  const mutation = useEntityMutation<ProfileFormValues, ProfilePage>({
    mutationFn: (values) =>
      api.profileUpdate.mutation({
        body: profileFormToBody(values, {
          surface,
          originalEmail: profile.email,
          meta: {
            identityVersion: profile.identityVersion,
            profileVersion: profile.profileVersion,
          },
        }),
      }),
    invalidateKeys: [profileKeys.detail(), profileKeys.session()],
    optimistic: {
      queryKey: profileKeys.detail(),
      update: (old) => {
        if (old?.status !== 200) return old;
        const v = form.getValues();
        return {
          ...old,
          body: {
            ...old.body,
            givenName: v.givenName.trim() || null,
            familyName: v.familyName.trim() || null,
            nickname: v.nickname.trim() || null,
            phone: v.phone.trim() || null,
            discordId: v.discordId.trim() || null,
            notifyEmail: v.notifyEmail,
            notifyInApp: v.notifyInApp,
            ...(isEmailChanged(v, profile.email) ? { email: v.email.trim() } : {}),
          },
        };
      },
    },
    onFieldErrors: (field, messages) => {
      if (field in form.getValues()) {
        form.setError(field as keyof ProfileFormValues, { message: messages.join(', ') });
      }
    },
    messages: {
      success: t('toast:profile.saved'),
      conflict: t('toast:profile.taken'),
      forbidden: t('toast:profile.stepUpFailed'),
      validation: t('toast:profile.validation'),
      error: t('toast:profile.saveError'),
    },
  });

  const onSubmit = (values: ProfileFormValues): void => {
    // E-mail step-up KLIENS-kapu: ha az e-mail változott, a jelenlegi jelszó KÖTELEZŐ — inline, mielőtt 403-at kapnánk.
    if (isEmailChanged(values, profile.email) && values.currentPassword.trim() === '') {
      form.setError('currentPassword', { message: t('validation:profile.stepUpRequired') });
      return;
    }
    mutation.mutate(values, {
      onSuccess: () => {
        // A step-up jelszót azonnal ürítjük (write-only), és a navbart is frissítjük (token újra-mintázás → friss claim-ek).
        form.setValue('currentPassword', '');
        void tryRefresh();
        // Az Asana PAT is ugyanennek a mentésnek a részeként áll be (ha adtak meg újat).
        void commitAsanaPat();
        // Ugyanígy az „Egyéb adatok" (ha változtak) — a saját végpontján, saját verzióval.
        void commitPersonal();
      },
    });
  };

  return (
    // MEGSZEMÉLYESÍTÉS-FAL: az Odin a `/iam/me/profile` PATCH-et is `@DenyImpersonation()`-nel zárja, tehát
    // más nevében a MENTÉS 403-ba futna. A user a jelszót és a 2FA-t kérte tiltásra — a
    // profil-adatok azért kerültek MELLÉ, mert a szerver-fal MÉRVE ugyanaz: enélkül a támogató végigtölti az
    // űrlapot, és a mentésnél kapja az elutasítást (ugyanaz a hibaosztály, csak másik kártyán).
    <ImpersonationFence>
      <Form {...form}>
        <div className="space-y-8">
          <ProfileSection title={t('profile.basics.title')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField<ProfileFormValues>
                name="givenName"
                label={t('profile.basics.givenName')}
                autoComplete="given-name"
              />
              <TextField<ProfileFormValues>
                name="familyName"
                label={t('profile.basics.familyName')}
                autoComplete="family-name"
              />
              {/* a BECENÉV mindkét felületen (belső + portál) — a megszólítás forrása (`addressName`) */}
              <div className="sm:col-span-2">
                <TextField<ProfileFormValues>
                  name="nickname"
                  label={t('profile.basics.nickname')}
                  hint={t('profile.basics.nicknameHint')}
                  autoComplete="nickname"
                />
              </div>
            </div>
            {/* PRÓBAIDŐ (#111) — csak olvasható infó a rész végén; a BFF csak a még el nem múlt lejáratot adja */}
            {showPersonal && personal?.probationEndDate && (
              <p className="text-muted-foreground mt-3 text-sm">
                {t('profile.basics.probationEnds', {
                  date: formatDay(personal.probationEndDate, i18n.language),
                })}
              </p>
            )}
          </ProfileSection>

          <ProfileSection title={t('profile.contact.title')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField<ProfileFormValues>
                name="email"
                label={t('profile.contact.email')}
                type="email"
                autoComplete="email"
                disabled={isPartner}
                {...(isPartner ? { hint: t('profile.contact.emailPartnerHint') } : {})}
              />
              <TextField<ProfileFormValues>
                name="phone"
                label={t('profile.contact.phone')}
                type="tel"
                autoComplete="tel"
              />
              {/* Step-up: CSAK e-mail-váltáskor jelenik meg — a jelenlegi jelszó megerősítése kötelező. */}
              {emailChanged && !isPartner && (
                <div className="sm:col-span-2">
                  <PasswordField<ProfileFormValues>
                    name="currentPassword"
                    label={t('profile.contact.stepUpLabel')}
                    hint={t('profile.contact.stepUpHint')}
                    autoComplete="current-password"
                    required
                  />
                </div>
              )}
            </div>
          </ProfileSection>

          {/* EGYÉB ADATOK — CSAK belső munkatársnak (a partner-portál Profilomán nincs). Saját al-űrlap: a
              mezői a saját `Form`-kontextusukból olvasnak, a validálásuk a közös Mentésnél fut. */}
          {showPersonal && (
            <Form {...personalForm}>
              <ProfileSection title={t('profile.personal.title')}>
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <DateField<PersonalFormValues>
                      name="birthDate"
                      label={t('profile.personal.birthDate')}
                    />
                    <DateField<PersonalFormValues>
                      name="nameDay"
                      label={t('profile.personal.nameDay')}
                    />
                  </div>
                  <PersonChildrenField<PersonalFormValues> name="children" />
                  <SwitchField<PersonalFormValues>
                    name="celebrationsOptOut"
                    label={t('profile.personal.celebrationsOptOut')}
                    hint={t('profile.personal.celebrationsOptOutHint')}
                  />
                </div>
              </ProfileSection>
            </Form>
          )}

          {!isPartner && (
            <ProfileSection title={t('profile.integrations.title')}>
              {/* Discord + Asana PAT — mindkettő TELJES szélességű, függőleges stack (egységes design). */}
              <div className="space-y-4">
                <TextField<ProfileFormValues>
                  name="discordId"
                  label={t('profile.integrations.discordId')}
                  hint={t('profile.integrations.discordIdHint')}
                  placeholder={t('profile.integrations.discordIdPlaceholder')}
                  autoComplete="off"
                />
                <div className="space-y-1.5">
                  {/* Label sor: bal a címke, jobbra CSAK a törlés kuka-ikon (piros) globális tooltippel — csak ha van
                  mentett token. A törlés a MENTETT PAT-ra vonatkozik (nem a beviteli mező tartalmára). */}
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="asana-pat">{t('profile.integrations.asanaLabel')}</Label>
                    {profile.asanaPatSet && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => void deleteAsanaPat()}
                            aria-label={t('profile.integrations.asanaDelete')}
                            className="text-destructive hover:text-destructive/80 focus-visible:ring-ring inline-flex items-center rounded-sm p-0.5 transition-colors focus:outline-none focus-visible:ring-2"
                          >
                            <TrashIcon className="size-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('profile.integrations.asanaDelete')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {/* Sima, LÁTHATÓ szöveg-mező (nincs szemecske): a meglévő PAT sosem tölthető vissza, beíráskor lássa a user.
                  Az érték a KÖZÖS „Mentés" gombbal áll be (nincs külön token-mentés gomb); üresen hagyva nincs írás. */}
                  <Input
                    id="asana-pat"
                    type="text"
                    value={asanaPat}
                    onChange={(e) => setAsanaPat(e.target.value)}
                    autoComplete="off"
                    placeholder={profile.asanaPatSet ? '••••••••••••' : undefined}
                  />
                  <p className="text-subtle text-xs">
                    {profile.asanaPatSet
                      ? t('profile.integrations.asanaHintSet')
                      : t('profile.integrations.asanaHintUnset')}
                  </p>
                </div>
              </div>
            </ProfileSection>
          )}

          <ProfileSection title={t('profile.notifications.title')}>
            <div className="space-y-3">
              <SwitchField<ProfileFormValues>
                name="notifyEmail"
                label={t('profile.notifications.email')}
                hint={t('profile.notifications.emailHint')}
              />
              <SwitchField<ProfileFormValues>
                name="notifyInApp"
                label={t('profile.notifications.inApp')}
                hint={t('profile.notifications.inAppHint')}
              />
            </div>
          </ProfileSection>

          {/* JOBBRA — az app kivétel nélküli szabálya: minden akció a konténere jobb végén (design.md). */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="brand"
              disabled={mutation.isPending}
              onClick={() =>
                void (async () => {
                  // az „Egyéb adatok" hibája is MEGÁLLÍTJA a mentést (ne menjen ki fél-adat); a fő űrlap mezői is
                  // kijelzik a saját hibáikat
                  const personalOk = !showPersonal || (await personalForm.trigger());
                  if (!personalOk) {
                    void form.trigger();
                    toast.error(t('toast:profile.validation'));
                    return;
                  }
                  await form.handleSubmit(onSubmit)();
                })()
              }
            >
              {mutation.isPending ? t('profile.saving') : t('profile.save')}
            </Button>
          </div>
        </div>
      </Form>
    </ImpersonationFence>
  );
}
